<template>
  <div class="profile-info-page" :class="showCollapse && 'collapse'">
    <div class="profile-info">
      <div v-if="!loading" class="profile-info-container">
        <div class="breadcrumb-navigation">
          <span class="navigation" @click="handleBreadCrumbNavigationClick">{{ $t(`admin.layout.newLayout.menu.potentialList`) }}</span>
          <span class="split">/</span>
          <span class="navigation active">{{ $t(`admin.layout.newLayout.menu.potentialProfile`) }}</span>
        </div>
        <div class="application-list-navigation">
          <el-button class="navigation-btn prev-btn" :disabled="currentPotentialIdIndex === 0" @click="handlePrevBtnClick">
            <i class="btn-icon el-icon-arrow-left"></i>
          </el-button>
          <span v-loading="candidateIdListLoading" class="position" element-loading-spinner="el-icon-loading">
            <span class="current">{{ (currentPotentialIdIndex + 1)|formatBigNumber }}</span>
            <span class="split">/</span>
            <span class="total">{{ totalCount|formatBigNumber }}</span>
          </span>
          <el-button class="navigation-btn next-btn" :disabled="(currentPotentialIdIndex + 1) === totalCount" @click="handleNextBtnClick">
            <i class="btn-icon el-icon-arrow-right"></i>
          </el-button>
        </div>
        <div class="base-info-container">
          <BaseInfo
            isPotential
            hiddenCandidateStatus
            :isDisabled="isDisabled"
            :candidateInfo="candidateInfo"
            :statusGroupList="statusGroupList"
            :isTalentPool="isTalentPoolCandidate"
          />
        </div>
        <div class="detail-info-container" ref="detailInfoContainer">
          <TabCard
            class="info-tab-card"
            :activeTab="activeTab"
            :tabList="tabList"
            @change="onTabChange"
          >
            <component v-bind="activeTab.props" v-on="activeTab.events" isPotential :is="activeTab.component" :isTalentPool="isTalentPoolCandidate" :candidateBaseInfo="candidateInfo" :isDisabled="isDisabled"></component>
          </TabCard>
        </div>
        <div class="fixed-list-navigation" :style="fixedNavigationStyle">
          <el-button class="navigation-btn prev-btn" :disabled="currentPotentialIdIndex === 0" @click="handlePrevBtnClick">
            <i class="btn-icon iconfont iconArrows"></i>
          </el-button>
          <el-button class="navigation-btn next-btn" :disabled="(currentPotentialIdIndex + 1) === totalCount" @click="handleNextBtnClick">
            <i class="btn-icon iconfont iconArrows"></i>
          </el-button>
        </div>
      </div>
      <CollapseInfo
        v-if="!loading && !candidateInfo.is_agency_uploaded_and_hid"
        ref="collapseInfo"
        class="collapse-container"
        :isPotential="true"
        :isTalentPool="isTalentPoolCandidate"
        :isMyComment="isMyComment"
        :candidateBaseInfo="candidateInfo"
        @open="handleOpen"
        @close="handleClose"
      />
    </div>
  </div>
</template>

<script>
import CollapseInfo from '@/views/adminNew/candidate/profile/components/collapseInfo.vue'
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import BaseInfo from '../profile/components/baseInfo.vue'
import TabCard from '../profile/components/tabCard.vue'
import Resume from '../profile/resume.vue'
import ReferralTab from '../profile/referralTab.vue'
import ActivityTracker from '../profile/activityTracker.vue'
import Tag from '../profile/tag.vue'
import moment from 'moment'

const PAGE_TYPE = {
  RESUME: 'resume',
  REFERRAL_CHAIN: 'referral-chain',
  ACTIVITY_TRACKER: 'activity-tracker',
  TAG: 'tag'
}

export default {
  components: {
    CollapseInfo,
    BaseInfo,
    TabCard
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      loading: false,
      activeTab: null,
      candidateInfo: {
        is_talent_pool_candidate: false
      },
      statusGroupList: [],
      statusList: [],
      defaultStatusKey: null,
      potentialIdList: [],
      candidateIdListLoading: false,
      pageDataLoader: null,
      fixedNavigationRightResetter: null,
      fixedNavigationRight: 110,
      showCollapse: false,
      isMyComment: false
    }
  },
  computed: {
    fixedNavigationStyle () {
      return {
        right: `${this.fixedNavigationRight}px`
      }
    },
    talentFilterGroupId () {
      return this.$route.params.talentFilterGroupId
    },
    jobId () {
      return this.$route.params.jobId
    },
    fromJobPotential () {
      if (this.$route.params.jobId) {
        return true
      } else {
        return false
      }
    },
    isTalentPoolCandidate () {
      return this.candidateInfo.is_talent_pool_candidate
    },
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    },
    isDisabled () {
      return this.hasOnlyActionOnMyCandidatePermission && !this.candidateInfo?.is_my_job
    },
    tabList () {
      const tabList = [
        {
          pageType: PAGE_TYPE.RESUME,
          component: Resume,
          suffix: 'resumeScore'
        },
        {
          pageType: PAGE_TYPE.REFERRAL_CHAIN,
          component: ReferralTab
        },
        {
          pageType: PAGE_TYPE.ACTIVITY_TRACKER,
          component: ActivityTracker,
          props: {
            ref: 'activityTracker'
          }
        },
        {
          pageType: PAGE_TYPE.TAG,
          component: Tag,
          permission: ['PREMIUM_TAG_MANAGEMENT']
        }
      ]
      const hidePageType = []
      if (!this.isTalentPoolCandidate) {
        hidePageType.push(PAGE_TYPE.RESUME)
      } else {
        hidePageType.push(PAGE_TYPE.REFERRAL_CHAIN)
      }
      return _.chain(tabList).filter(tab => {
        if (hidePageType.includes(tab.pageType)) {
          return false
        }
        if (tab.permission) {
          return this.hasAnyPermission(tab.permission)
        }
        return true
      }).map(tab => {
        const pageTypeKey = _.findKey(PAGE_TYPE, pageType => pageType === tab.pageType)
        tab.labelI18nKey = `${this.i18nPrefix}.tabMenu.${pageTypeKey}`
        return tab
      }).value()
    },
    pageType () {
      return this.$route.params.pageType
    },
    candidateId () {
      return this.$route.query.candidate_id
    },
    potentialId () {
      return this.$route.params.potentialId
    },
    listPageParams () {
      return this.$route.query.list_params
    },
    pageTitle () {
      const title = this.$t(`${this.i18nPrefix}.title.detailResume`)
      const tabLabel = this.activeTab && this.$t(this.activeTab.labelI18nKey)
      const candidateName = this.candidateInfo?.other_name
      return _.chain([candidateName, title, tabLabel]).filter().join(' - ').value()
    },
    totalCount () {
      return this.potentialIdList.length
    },
    currentPotentialIdIndex () {
      return _.indexOf(this.potentialIdList, this.candidateInfo.potential_id)
    }
  },
  created () {
    this.$eventBus.$on('showCollapseInfo', (activeNameList) => this.showCollapseInfo(activeNameList))
    this.$eventBus.$on('changeIsMyCommentsStatus', (status) => {
      this.isMyComment = status
    })
    this.$eventBus.$on('onCommentAdded', (comment, callback) => this.onCommentAdded(comment, callback))
    this.pageDataLoader = this.initPageData()
    this.loadPotentialIdList()
  },
  mounted () {
    this.pageDataLoader.then(() => {
      this.$nextTick(() => {
        this.resetFixedNavigationRight()
      })
    })
    this.fixedNavigationRightResetter = _.debounce(this.resetFixedNavigationRight, 10)
    window.addEventListener('resize', this.fixedNavigationRightResetter)
  },
  destroyed () {
    window.removeEventListener('resize', this.fixedNavigationRightResetter)
  },
  methods: {
    ...mapActions('potential', ['getPotentialCandidateBaseInfo', 'getPotentialCandidateIdList']),
    resetFixedNavigationRight () {
      const rect = this.$refs.detailInfoContainer.getBoundingClientRect()
      const right = window.innerWidth - rect.left - rect.width + 68
      this.fixedNavigationRight = _.max([right, 104])
    },
    refreshActiveTab () {
      let pageType = null
      if (!this.pageType) {
        pageType = this.tabList[0].pageType
      }
      let path = null
      if (this.fromJobPotential) {
        path = `/v-admin/job/${this.jobId}/candidate/${this.potentialId}/potential-profile/${pageType}`
      } else {
        path = `/v-admin/talent-pool/${this.talentFilterGroupId}/candidate/${this.potentialId}/potential-profile/${pageType}`
      }
      if (!this.pageType) {
        this.$router.push({ path: path, query: this.$route.query })
        return
      }
      if (this.activeTab && this.activeTab.pageType === this.pageType) {
        return
      }
      const tab = _.find(this.tabList, { pageType: this.pageType })
      if (!tab) {
        const pageType = this.tabList[0].pageType
        if (this.fromJobPotential) {
          path = `/v-admin/job/${this.jobId}/candidate/${this.potentialId}/potential-profile/${pageType}`
        } else {
          path = `/v-admin/talent-pool/${this.talentFilterGroupId}/candidate/${this.potentialId}/potential-profile/${pageType}`
        }
        this.$router.replace({ path: path, query: this.$route.query })
      } else if (tab.permission && !this.hasAnyPermission(tab.permission)) {
        this.$router.replace('/error/no-permission')
      } else {
        this.activeTab = tab
      }
    },
    async loadCandidateProfileInfo () {
      const resp = await this.getPotentialCandidateBaseInfo({ potentialId: this.potentialId })
      if (resp) {
        const mobile = resp.mobile || resp.second_phone
        const countryCode = resp.country_code
        const age = moment().diff(resp.birthday, 'year')
        createI18nProperty(resp, 'yearsOld', `${this.i18nPrefix}.label.yearsOld`, { age })
        resp.mobileWithCountryCode = _.chain([countryCode, mobile]).filter().join(' ').value()
        addI18nProperty(resp.root_company, 'i18nName', 'cn_name', 'name')
        this.candidateInfo = resp
      }
    },
    async loadPotentialIdList () {
      if (!this.listPageParams) {
        return
      }
      this.candidateIdListLoading = true
      const params = {
        list_params: this.listPageParams,
        is_talent_pool: !this.fromJobPotential,
        job_id: this.jobId
      }
      try {
        const resp = await this.getPotentialCandidateIdList(params)
        if (resp) {
          this.potentialIdList = resp
        }
      } finally {
        this.candidateIdListLoading = false
      }
    },
    async initPageData () {
      this.$eventBus.$emit('showLoading', true)
      this.loading = true
      try {
        await this.loadCandidateProfileInfo()
        const tab = _.find(this.tabList, { pageType: this.pageType })
        if (!tab) {
          this.activeTab = null
        }
        this.refreshActiveTab()
      } finally {
        this.loading = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onTabChange (tab) {
      const pageType = tab.pageType
      let path = null
      if (this.fromJobPotential) {
        path = `/v-admin/job/${this.jobId}/candidate/${this.potentialId}/potential-profile/${pageType}`
      } else {
        path = `/v-admin/talent-pool/${this.talentFilterGroupId}/candidate/${this.potentialId}/potential-profile/${pageType}`
      }
      if (this.$route.path === path) {
        return
      }
      this.$router.push({ path, query: this.$route.query })
    },
    handleBreadCrumbNavigationClick () {
      if (this.fromJobPotential) {
        this.$router.push(`/v-admin/job/${this.jobId}/potential/list`)
      } else {
        this.$router.push(`/v-admin/talent-pool/${this.talentFilterGroupId}/potential/list`)
      }
    },
    handlePrevBtnClick () {
      if (this.candidateIdListLoading) {
        return
      }
      const potentialId = this.potentialIdList[this.currentPotentialIdIndex - 1]
      if (!potentialId) {
        return
      }
      let path = null
      if (this.fromJobPotential) {
        path = `/v-admin/job/${this.jobId}/candidate/${potentialId}/potential-profile/`
      } else {
        path = `/v-admin/talent-pool/${this.talentFilterGroupId}/candidate/${potentialId}/potential-profile/`
      }
      this.$router.push({
        path: path,
        query: {
          ...this.$route.query,
          potential_id: potentialId
        }
      })
    },
    handleNextBtnClick () {
      if (this.candidateIdListLoading) {
        return
      }
      const potentialId = this.potentialIdList[this.currentPotentialIdIndex + 1]
      if (!potentialId) {
        return
      }
      let path = null
      if (this.fromJobPotential) {
        path = `/v-admin/job/${this.jobId}/candidate/${potentialId}/potential-profile/`
      } else {
        path = `/v-admin/talent-pool/${this.talentFilterGroupId}/candidate/${potentialId}/potential-profile/`
      }
      this.$router.push({
        path: path,
        query: {
          ...this.$route.query,
          potential_id: potentialId
        }
      })
    },
    onCommentAdded (comment, callback) {
      const activityTracker = this.$refs.activityTracker
      const collapseInfo = this.$refs.collapseInfo
      if (activityTracker) {
        activityTracker.onCommentAdded(comment)
      }
      if (collapseInfo) {
        collapseInfo.onCommentAdded(comment)
      }
      if (callback) {
        callback()
      }
    },
    showCollapseInfo (activeNameList) {
      const collapseInfo = this.$refs.collapseInfo
      if (collapseInfo) {
        collapseInfo.handelChangeShowCollapse(activeNameList)
      }
    },
    handleOpen (collapseWidth) {
      if (!this.showCollapse) {
        this.showCollapse = true
        this.fixedNavigationRight += collapseWidth
      }
    },
    handleClose (collapseWidth) {
      if (this.showCollapse) {
        this.showCollapse = false
        this.fixedNavigationRight -= collapseWidth
      }
    }
  },
  watch: {
    pageType () {
      this.refreshActiveTab()
    },
    potentialId () {
      this.initPageData()
    },
    candidateInfo () {
      this.refreshActiveTab()
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-info-page {
  padding: 20px 0;
  display: flex;
  height: 100%;

  .profile-info {
    flex: 1;
    display: flex;
  }

  &.collapse {
    padding-bottom: 0;

    .profile-info {
      margin-left: 12px;
      overflow: auto;
    }

    .collapse-container {
      margin-left: 12px;
    }
  }
}

.profile-info-container {
  display: flex;
  flex-direction: column;
  width: 95%;
  min-width: min-content;
  margin: 50px auto 0;
  position: relative;

  .breadcrumb-navigation {
    line-height: 25px;
    font-size: 18px;
    color: #606266;
    position: absolute;
    top: -47px;
    left: 0;

    .split {
      margin: 0 10px;
    }

    .navigation {
      cursor: pointer;
      color: #0096cc;

      &.active {
        cursor: default;
        color: #606266;
      }
    }
  }

  .application-list-navigation {
    position: absolute;
    top: -50px;
    right: 0;

    .navigation-btn {
      padding: 0 8px;
      line-height: 28px;
      border-radius: 2px;
      border-color: #c8c8c8;

      .btn-icon {
        font-size: 12px;
      }
    }

    .position  {
      margin: 0 56px;
      line-height: 25px;
      font-size: 18px;
      color: #606266;

      .split {
        margin: 0 10px;
      }
    }
  }

  .base-info-container {
    padding: 30px 40px;
    border-radius: 6px;
    background: #fff;
  }

  .detail-info-container {
    flex: 1;
    margin-top: 20px;
    padding: 25px;
    border-radius: 6px;
    background: #fff;

    .info-tab-card {
      min-height: 100%;

    }
  }

  .fixed-list-navigation {
    position: fixed;
    bottom: 48px;
    right: 124px;

    .navigation-btn {
      width: 40px;
      height: 40px;
      padding: 0 8px;
      border-radius: 50%;
      border: 0;
      box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.06), 0px 3px 6px 0px rgba(0,0,1,0.12);
      text-align: center;
      background: #ffffff;

      & + .navigation-btn {
        margin-left: 38px;
      }

      &:not(.is-disabled):hover {
        background: #0096cc;

        .btn-icon {
          color: #ffffff;
        }
      }

      &.prev-btn {
        .btn-icon {
          transform: rotateZ(180deg);
        }
      }

      .btn-icon {
        display: inline-block;
        font-size: 15px;
        color: #606266;

        &::before {
          padding-left: 2px;
        }
      }
    }
  }
}
</style>
